import config from '../../../../config';
import { formatError, LOGGER, Logger } from "../../../../packages/logger/src";
import { removeLocalStorageItem } from "../../../../packages/logger/src/LocalStorage";
// import { createCallHistoryClient, EVENT_KEYS } from "@webex/web-calling-sdk";
import { COMMON_EVENT_KEYS, createCallHistoryClient } from '@webex/calling';
import { DeleteCallHistoryRecordsResponse, EndTimeSessionId, UpdateMissedCallsResponse } from "@webex/component-adapter-interfaces/dist/esm/src";
import {
  DATE,
  IWebexCallHistoryResponse,
  LIMIT,
  SORT,
  SORT_BY
} from "../../../adapter-interfaces/src";
import { submitMetrics } from "../services/WebexMetricsService";
import { recentCallsListLatencyMetric } from "../utils/MetricUtils";
import { EventType, eventEmitter } from "../utils/eventEmitter";
import { getMockCallHistoryResponse } from "./WebexMockCallHistoryService";
import { getMockEnrichmentCallHistory } from "./WebexMockEnrichmentCallHistoryService";
import { whenDeviceRegistered, whenWebexReady } from "./WebexInstanceService";

// Global flag for enabling mock enrichment call history (set from browser console)
declare global {
  interface Window {
    USE_MOCK_ENRICHMENT_CALL_HISTORY?: boolean;
  }
}

// Set this flag to true to enable mock call history data in any environment
const isDev = config.APP_ENVIRONMENT === 'Dev_Environment';
export let USE_MOCK_CALL_HISTORY = false; // Enable for AI Summary mock testing
/**
 *
 */
let makeRegister = true;

export type CallHistoryFetchOptions = {
  /** Maximum number of records to request. Set to null to request without limit. */
  limit?: number | null;
  /** Date range in days to request. Defaults to 30 days (DATE.MONTH). */
  dateRange?: DATE;
  /** Optional ISO timestamp to request records since that time; will be converted to a capped dateRange. */
  since?: string;
};

export async function initRecentCalls(options: CallHistoryFetchOptions = {}): Promise<IWebexCallHistoryResponse> {
  // Check for mock enrichment call history first (for phone enrichment testing)
  if (typeof window !== 'undefined' && window.USE_MOCK_ENRICHMENT_CALL_HISTORY) {
    Logger.info('[CallHistory] Using MOCK ENRICHMENT call history for testing');
    return getMockEnrichmentCallHistory();
  }

  // Use file-level flag for regular mock mode
  if (USE_MOCK_CALL_HISTORY) {
    // Clear call history cache to always force a fetch
    //removeLocalStorageItem('callHistoryCachedData');
    const fullFetch = options.limit === null;
    return getMockCallHistoryResponse({ fullFetch });
  }

  let recentCallsListLatency;
  const startTime = new Date();
  let endTime;
  let response;

  const {
    limit = LIMIT.MAX,
    dateRange = DATE.MONTH,
    since,
  } = options;

  // If caller supplied a since timestamp, compute the number of days to request
  // and cap it to DATE.MONTH (30 days). The SDK expects a dateRange in days.
  let resolvedDateRange = dateRange;
  if (since) {
    const sinceTs = Date.parse(since);
    if (!Number.isNaN(sinceTs)) {
      const now = Date.now();
      
      // CRITICAL FIX: Reject future dates
      if (sinceTs > now) {
        Logger.info(`[CallHistoryService] initRecentCalls: Invalid since (future date): ${since}, falling back to dateRange=${dateRange}`);
        resolvedDateRange = dateRange;
      } else {
        const diffMs = now - sinceTs;
        // Use floor to avoid off-by-one from small timing differences (tests/runtime).
        const days = Math.max(1, Math.floor(diffMs / (24 * 60 * 60 * 1000)));
        // Cap to DATE.MONTH
        resolvedDateRange = Math.min(days, DATE.MONTH);
        
        // Warn if requesting very old data
        if (days > DATE.MONTH) {
          Logger.info(`[CallHistoryService] initRecentCalls: Requested ${days} days of history (since=${since}), capped at ${DATE.MONTH} days`);
        } else {
          Logger.info(`[CallHistoryService] initRecentCalls: Converted since=${since} to dateRange=${resolvedDateRange} days (computed=${days}, capped at ${DATE.MONTH})`);
        }
      }
    } else {
      // invalid since value — fallback to provided dateRange or default month
      Logger.info(`[CallHistoryService] initRecentCalls: Invalid since value (NaN), falling back to dateRange=${dateRange}`);
      resolvedDateRange = dateRange;
    }
  } else {
    Logger.info(`[CallHistoryService] initRecentCalls: No since param, using dateRange=${resolvedDateRange} days, limit=${limit}`);
  }

  const callHistoryClient: any = createCallHistoryClient((window as any).webex, {
    level: LOGGER.INFO,
  });
  if (makeRegister) {
    callHistoryClient.on(
      COMMON_EVENT_KEYS.CALL_HISTORY_USER_SESSION_INFO,
      (sessionData: any) => {
        Logger.info(
          `Users recent session data from service: '${sessionData.data.userSessions.userSessions[0]}'`
        );
        eventEmitter.dispatch(EventType.AUTO_REFRESH, "autorefresh");
      }
    );
    callHistoryClient.on(
      COMMON_EVENT_KEYS.CALL_HISTORY_USER_VIEWED_SESSIONS,
      (sessionData: any) => {
        Logger.info(
          `Users read session data from service: '${sessionData.data.userReadSessions.userReadSessions[0]}'`
        );
        eventEmitter.dispatch(EventType.AUTO_REFRESH, "autorefresh");
      }
    );
    callHistoryClient.on(
      COMMON_EVENT_KEYS.CALL_HISTORY_USER_SESSIONS_DELETED,
      (sessionData: any) => {
        Logger.info(
          `Users delete session data from service  '${sessionData?.data?.eventType}'`
        );
        eventEmitter.dispatch(EventType.AUTO_REFRESH, "autorefresh");
      }
    );
    makeRegister = false;
  }
  const resolvedLimit = limit === null ? undefined : limit;

  response = await callHistoryClient.getCallHistoryData(
    resolvedDateRange,
    resolvedLimit,
    SORT.DEFAULT,
    SORT_BY.DEFAULT
  );
  //TODO:: 
  endTime = new Date();
  recentCallsListLatency =
    Math.floor(endTime.getTime() - startTime.getTime()) / 1000;
  submitMetrics(
    {
      metricEvent: recentCallsListLatencyMetric,
      recentCallsListLatency: `${recentCallsListLatency}`,
    }
  );
  return response as unknown as IWebexCallHistoryResponse;

}

export async function listRecentCallEvents(
  options: CallHistoryFetchOptions = {}
): Promise<IWebexCallHistoryResponse> {
  await whenWebexReady(20000);
  await whenDeviceRegistered(20000);
  return initRecentCalls(options);
}

export async function updateMissedCalls(
  endTimeSessionIds: EndTimeSessionId[]
): Promise<UpdateMissedCallsResponse> {
  const callHistoryClient = createCallHistoryClient((window as any).webex, {
    level: LOGGER.INFO,
  });
  try {
    const response = await callHistoryClient.updateMissedCalls(endTimeSessionIds);
    Logger.info(`WebexCallHistoryService:updateMissedCalls(): done.`);
    // Return the response which should be of type UpdateMissedCallsResponse
    return response as UpdateMissedCallsResponse;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}

export async function deleteCallHistoryRecords(
  deleteSessionIds: EndTimeSessionId[]
): Promise<DeleteCallHistoryRecordsResponse> {
  const callHistoryClient = createCallHistoryClient((window as any).webex, {
    level: LOGGER.INFO,
  });
  try {
    const response = await callHistoryClient.deleteCallHistoryRecords(deleteSessionIds);
    Logger.info(`WebexCallHistoryService:deleteCallHistoryRecords(): done.`);
    // Return the response which should be of type DeleteCallHistoryRecordsResponse
    return response as DeleteCallHistoryRecordsResponse;
  } catch (error) {
    Logger.error(
      `WebexCallHistoryService:deleteCallHistoryRecords: got error: '${formatError(
        error
      )}'.`
    );
    throw error;
  }
}
